import { usePrinterPreview } from '../../lib/printers/usePrinterPreview.js';
import { toUserMessage } from '../../lib/errors.js';
// ProductionDayPage — Scheda di produzione giornaliera (T-215 + T-229, modulo
// ristorazione collettiva). È il "batch record" del centro cottura (BRCGS 9
// §3.9 / IFS 8 §4.18, Reg. 852/2004 art. 5): per ogni pasto del giorno i dati
// industriali (porzioni, servizio, menù, CCP cottura al cuore) + gli
// ingredienti/lotti consumati; pasto campione PER PRODOTTO con stampa
// etichetta dedicata (clausola capitolati); totale pasti auto-calcolato;
// blocchi alla chiusura (checklist per prodotto); chiusura con firma
// operatore + verifica di 2° livello (BRCGS 2.10.2) + riapertura supervisore.
// Offline-first: i draft passano dalla coda master; close/verify/reopen usano
// un outbox RPC atomico (testata + fatto audit + receipt). Solo l'export
// PDF/email è online-only (edge function).

import {
  IonAccordion,
  IonAccordionGroup,
  IonAlert,
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonDatetime,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonNote,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTextarea,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import {
  alertCircle,
  arrowForwardOutline,
  calendarOutline,
  checkmarkCircle,
  chevronBackOutline,
  chevronForwardOutline,
  closeOutline,
  documentTextOutline,
  lockClosedOutline,
  lockOpenOutline,
  mailOutline,
  printOutline,
  saveOutline,
  shieldCheckmarkOutline,
} from 'ionicons/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { MealService, ProductionDay } from '@trackeat/shared';
import { MEAL_SERVICES, productionDayIdFor, flattenInputLots } from '@trackeat/shared';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '../../lib/adapters/share/index.js';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import {
  useCollettivaEnabled,
  useEnqueueMaster,
  useMasters,
} from '../../lib/data/queries.js';
import { getMeta } from '../../lib/db/local.js';
import {
  isProductionDayEditableV1,
  normalizeProductionDaySignedSnapshotV1,
  productionDayConflictRefreshSignalV1,
  type ProductionDaySignedSnapshotV1,
} from '../../lib/productionDayTransition.js';
import { getSyncEngine } from '../../lib/sync/engine.js';
import { useSyncState } from '../../lib/sync/SyncProvider.js';
import { createPrinterAdapter, PREVIEW_PRINTER_ID, PREVIEW_PRINTER_NAME } from '../../lib/adapters/printers/index.js';
import { CAMPIONE_TEMPLATE, buildCampioneLabelData } from '../../lib/labels/campioneTemplate.js';
import { getSupabase, SUPABASE_URL } from '../../lib/supabase.js';
import { stockEventMatchesBusiness } from '../../lib/stockEventBusinessScope.js';

// Riga locale: il mirror SQLCipher può contenere righe inserite offline che non
// hanno ancora `version`/`created_at` server-side → opzionali qui.
// reopen_count/reopened_at: colonne additive T-229 (migrazione collettiva_v12,
// già live) non ancora nello Zod shared → opzionali locali.
type ProductionDayRow = Omit<ProductionDay, 'version' | 'created_at'> & {
  version?: number;
  created_at?: string;
  reopen_count?: number;
  reopened_at?: string | null;
};

interface ProductionInputRow {
  food_name?: string | null;
  batch_lot?: string | null;
  qty_used?: number;
  uom?: string;
  supplier_name?: string | null;
  expiration_date?: string | null;
  // T-452: input multi-lotto (collettiva) → espanso a una riga per lotto al render.
  lots?: Array<{ batch_lot?: string | null; expiration_date?: string | null }> | null;
}

// T-349 Fase 3: da questa data in poi (compresa) la scheda giornaliera dei
// tenant collettiva mostra SOLO le produzioni materializzate dal piano.
// I giorni precedenti restano label-driven (storico HACCP immutato).
const COLLETTIVA_PLAN_ONLY_FROM = '2026-07-01';

interface ProductionRow {
  id: string;
  business_id?: string | null;
  output_name: string;
  output_qty: number | null;
  output_uom: string | null;
  produced_at: string;
  inputs?: ProductionInputRow[];
  // T-215 — colonne additive "dati industriali"
  portions?: number | null;
  meal_service?: MealService | null;
  menu_label?: string | null;
  ccp_core_temp_c?: number | null;
  ccp_notes?: string | null;
  // T-221/T-229 — lotto di produzione + prelievo campione per prodotto
  lp_code?: string | null;
  sample_taken_at?: string | null;
  // T-349 — produzione materializzata dal piano ("Pianifica produzione").
  // NULL = creata in altro modo (etichetta/manuale).
  source_production_plan_item_id?: string | null;
  // T-380 — produzione da PRE-STAMPA serale (recipe-linked, senza porzioni,
  // in attesa che gli ordini del mattino le attacchino le quantità).
  prestampa?: boolean | null;
  recipe_id?: string | null;
  version?: number;
  archived_at?: string | null;
}

// Stampanti configurate (master `printers`). Subset dei campi usati qui.
interface PrinterRow {
  id: string;
  name: string;
  driver: 'brother_ql' | 'zpl' | 'escpos' | 'tspl' | 'cpcl' | 'niimbot' | 'preview';
  connection: 'bluetooth' | 'ble' | 'wifi' | 'usb';
  address: string;
  business_id?: string | null;
  active?: boolean;
  archived_at?: string | null;
}

// DDT del giorno (master `shipments`) — solo per il warning non bloccante
// "Nessun DDT emesso per questa giornata".
interface ShipmentRow {
  id: string;
  business_id?: string | null;
  kind?: 'ddt' | 'trasferimento';
  status?: 'draft' | 'issued' | 'void';
  doc_number?: number | null;
  issued_at?: string | null;
  created_at?: string;
  archived_at?: string | null;
}

const MEAL_LABELS: Record<MealService, string> = {
  colazione: 'Colazione',
  pranzo: 'Pranzo',
  cena: 'Cena',
};

const SAMPLE_NOTES_PLACEHOLDER =
  '≥ 150 g per preparazione, conservare a +4 °C per 72 h';

// ───────── etichetta CAMPIONE (T-229) ─────────
// T-279: template + helper spostati in lib/labels/campioneTemplate.ts per
// condividerli con LabelsPage (sezione "ristampa campione" nel dialog di
// ristampa del lotto di base). Qui si importano CAMPIONE_TEMPLATE +
// buildCampioneLabelData.

function localDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function localDateKeyFromIso(iso: string): string {
  return localDateKey(new Date(iso));
}

/**
 * Parse del campo "Pasti totali": stringa vuota → null, numero valido →
 * intero arrotondato, altrimenti 'invalid'. Unica fonte sia per il commit
 * on-blur sia per il payload di chiusura (mai due parser divergenti).
 */
function parseMealsTotal(raw: string): number | null | 'invalid' {
  const s = raw.trim();
  if (!s) return null;
  const n = Number(s.replace(',', '.'));
  if (!Number.isFinite(n) || n < 0) return 'invalid';
  return Math.round(n);
}

function formatDateLabel(key: string): string {
  // Mezzogiorno locale per evitare slittamenti di giorno con il fuso.
  const d = new Date(`${key}T12:00:00`);
  return d.toLocaleDateString('it-IT', {
    weekday: 'short',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== 'string') return reject(new Error('FileReader did not return a string'));
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(blob);
  });
}

// ───────── stile "form sezionato" ─────────
const cardStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 14,
  boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
  margin: '10px 0',
  padding: '12px 14px',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  color: '#475569',
  textTransform: 'uppercase',
  letterSpacing: '.05em',
  margin: '20px 2px 4px',
};

const infoBannerStyle: React.CSSProperties = {
  background: '#eff6ff',
  border: '1px solid #bfdbfe',
  borderRadius: 12,
  padding: '10px 14px',
  margin: '10px 0',
  fontSize: 13,
  color: '#1e40af',
};

const warnBannerStyle: React.CSSProperties = {
  background: '#fffbeb',
  border: '1px solid #fde68a',
  borderRadius: 12,
  padding: '10px 14px',
  margin: '10px 0',
  fontSize: 13,
  color: '#92400e',
};

const successBannerStyle: React.CSSProperties = {
  background: '#ecfdf5',
  border: '1px solid #a7f3d0',
  borderRadius: 12,
  padding: '10px 14px',
  margin: '12px 0',
};

const primaryCtaStyle: React.CSSProperties = {
  '--border-radius': '12px',
  height: 52,
  fontWeight: 700,
  marginTop: 12,
} as React.CSSProperties;

const menuBadgeStyle: React.CSSProperties = {
  background: '#eef2ff',
  color: '#4338ca',
  border: '1px solid #c7d2fe',
  borderRadius: 999,
  padding: '1px 8px',
  fontSize: 11,
  fontWeight: 700,
  marginLeft: 6,
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
};

// Riga della checklist requisiti per prodotto: ✓ verde / ! ambra.
function CheckChip({ ok, label }: { ok: boolean; label: string }): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 12,
        fontWeight: 600,
        color: ok ? '#047857' : '#b45309',
      }}
    >
      <IonIcon
        icon={ok ? checkmarkCircle : alertCircle}
        style={{ color: ok ? '#10b981' : '#f59e0b', fontSize: 16, flexShrink: 0 }}
      />
      {label}
    </div>
  );
}

// Checklist requisiti di chiusura per singola produzione (T-229 punto 1).
interface ProdCheck {
  temp: boolean;
  portions: boolean;
  service: boolean;
  sample: boolean;
  ok: boolean;
}

export function ProductionDayPage(): JSX.Element {
  const queryClient = useQueryClient();
  const syncState = useSyncState();
  const collettivaEnabled = useCollettivaEnabled();
  const { profile } = useAuth();
  const { data: days = [] } = useMasters<ProductionDayRow>('production_days');
  const { data: productions = [] } = useMasters<ProductionRow>('productions');
  const { data: dbPrinters = [] } = useMasters<PrinterRow>('printers');
  const { data: shipments = [] } = useMasters<ShipmentRow>('shipments');
  const {
    data: businesses = [],
    isLoading: businessesLoading,
    isError: businessesError,
  } = useMasters<{
    id: string;
    tenant_id?: string | null;
    archived_at?: string | null;
  }>('businesses');
  // T-422: tipo CCP per ricetta (cottura ≥74°C / freddo=facoltativo). Serve a
  // sapere quali produzioni RICHIEDONO la temperatura al cuore e quali no.
  const { data: recipesForCcp = [] } = useMasters<{ id: string; ccp_type?: string | null }>('recipes');
  const ccpTypeByRecipe = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of recipesForCcp) m.set(r.id, r.ccp_type ?? 'cottura');
    return m;
  }, [recipesForCcp]);
  // Una produzione è "servita fredda" se la sua ricetta ha ccp_type='freddo'.
  const isColdProd = useMemo(
    () => (p: ProductionRow): boolean => (p.recipe_id ? ccpTypeByRecipe.get(p.recipe_id) === 'freddo' : false),
    [ccpTypeByRecipe],
  );
  const enqueueMaster = useEnqueueMaster();

  const [selectedDate, setSelectedDate] = useState<string>(() => localDateKey(new Date()));
  const [dateOpen, setDateOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [busyExport, setBusyExport] = useState(false);
  const [busyClose, setBusyClose] = useState(false);
  const [busyConflictResolution, setBusyConflictResolution] = useState(false);
  const [dayConflict, setDayConflict] = useState<{
    reason: string;
  } | null>(null);

  // Modale "Dati industriali" sul singolo pasto
  const [editingProd, setEditingProd] = useState<ProductionRow | null>(null);
  const [prodForm, setProdForm] = useState({
    portions: '',
    mealService: '' as MealService | '',
    ccpTemp: '',
    ccpNotes: '',
  });

  // Conferme
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [verifyConfirmOpen, setVerifyConfirmOpen] = useState(false);
  const [reopenConfirmOpen, setReopenConfirmOpen] = useState(false);
  const [emailAlertOpen, setEmailAlertOpen] = useState(false);

  // Draft dei campi di giornata (commit on blur)
  const [sampleNotesDraft, setSampleNotesDraft] = useState('');
  const [mealsTotalDraft, setMealsTotalDraft] = useState('');
  const [mealsNotesDraft, setMealsNotesDraft] = useState('');
  const [notesDraft, setNotesDraft] = useState('');

  // T-229 — pasto campione per prodotto
  const [busySampleId, setBusySampleId] = useState<string | null>(null);
  const [samplePrinterId, setSamplePrinterId] = useState<string | null>(null);
  // T-424 — anteprima in-app dell'etichetta campione (stampante 'preview' demo).
  const [inAppPreview, setInAppPreview] = usePrinterPreview('/warehouse/production-day');
  // Override locale sample_taken_at: copre la finestra tra l'enqueue del patch
  // e la rilettura del mirror da parte di React Query (stesso razionale di
  // localDayRef sotto). Keyed per production id (univoco cross-giornata).
  const [sampleLocal, setSampleLocal] = useState<Record<string, string>>({});

  // Versioni OCC delle productions già patchate localmente (modal "Dati
  // industriali" + prelievo campione sulla stessa riga in rapida successione:
  // senza, il secondo patch partirebbe con base_version stantia →
  // version_conflict silenzioso).
  const localProdVerRef = useRef<Record<string, number>>({});

  // Stato locale dei patch già ACCODATI per la giornata selezionata. Include
  // anche l'effetto ottimistico close/verify/reopen, così la firma congela
  // subito la UI prima del refresh React Query dal mirror SQLite.
  const localDayRef = useRef<{
    date: string;
    id: string;
    version: number;
    patches: Record<string, unknown>;
  } | null>(null);

  const businessId = profile?.active_business_id ?? null;
  const activeTenantBusinesses = useMemo(
    () => businesses.filter((business) =>
      !business.archived_at
      && (business.tenant_id == null || business.tenant_id === profile?.active_tenant_id)),
    [businesses, profile?.active_tenant_id],
  );
  const allowLegacyUnscopedBusiness = !businessesLoading
    && !businessesError
    && businessId !== null
    && activeTenantBusinesses.length === 1
    && activeTenantBusinesses[0]?.id === businessId;
  const dayConflictRefreshSignal =
    productionDayConflictRefreshSignalV1(syncState);

  const dayRow = useMemo<ProductionDayRow | null>(() => {
    return (
      days.find(
        (d) =>
          d.sheet_date === selectedDate &&
          !d.archived_at &&
          stockEventMatchesBusiness(
            { business_id: d.business_id },
            businessId,
            allowLegacyUnscopedBusiness,
          ),
      ) ?? null
    );
  }, [days, selectedDate, businessId, allowLegacyUnscopedBusiness]);

  // I conflitti terminali sono persistenti nell'outbox e sopravvivono al
  // restart. Vanno quindi riletti dal DB locale sia all'apertura pagina sia a
  // ogni avanzamento del sync, non affidati a uno stato React effimero.
  useEffect(() => {
    let active = true;
    const tenantId = profile?.active_tenant_id ?? null;
    if (!tenantId || !businessId) {
      setDayConflict(null);
      return () => {
        active = false;
      };
    }
    void getSyncEngine().latestProductionDayConflict({
      tenantId,
      businessId,
      sheetDate: selectedDate,
    }).then(async (conflict) => {
      if (!active) return;
      setDayConflict(
        conflict
          ? { reason: conflict.conflict?.reason ?? 'conflitto server' }
          : null,
      );
      if (conflict) {
        // Il terminal reject ha già ritirato lo strato ottimistico. Elimina
        // anche l'override in-memory e forza la vista sulla copia locale
        // autorevole, così non resta una falsa scheda "chiusa" fino al reload.
        localDayRef.current = null;
        await queryClient.invalidateQueries({ queryKey: ['masters'] });
      }
    }).catch(() => undefined);
    return () => {
      active = false;
    };
  }, [
    businessId,
    profile?.active_tenant_id,
    queryClient,
    selectedDate,
    dayConflictRefreshSignal,
  ]);

  const dayProductions = useMemo<ProductionRow[]>(() => {
    return productions
      .filter((p) => !!p.produced_at && localDateKeyFromIso(p.produced_at) === selectedDate)
      .filter((p) => !p.archived_at)
      .filter((p) => stockEventMatchesBusiness(
        { business_id: p.business_id },
        businessId,
        allowLegacyUnscopedBusiness,
      ))
      // T-349 Fase 3 (decisione owner): in collettiva la scheda giornaliera si
      // popola SOLO dal piano (ordini accettati → Pianifica produzione), non più
      // dalle etichette stampate. Le produzioni nate da etichetta restano per la
      // tracciabilità (QR) ma non entrano nella scheda. Cutoff: i giorni PRIMA
      // dell'attivazione mantengono lo storico label-driven (registro HACCP già
      // chiuso/firmato — non deve svuotarsi retroattivamente).
      .filter((p) =>
        !collettivaEnabled
        || selectedDate < COLLETTIVA_PLAN_ONLY_FROM
        // dal piano OPPURE pre-stampate (T-380): entrambe sono "produzione
        // pianificata"; le sole label-driven restano fuori dalla scheda.
        || !!p.source_production_plan_item_id
        || !!p.prestampa)
      .sort((a, b) => a.produced_at.localeCompare(b.produced_at));
  }, [
    productions,
    selectedDate,
    businessId,
    allowLegacyUnscopedBusiness,
    collettivaEnabled,
  ]);

  const groups = useMemo(() => {
    const colazione: ProductionRow[] = [];
    const pranzo: ProductionRow[] = [];
    const cena: ProductionRow[] = [];
    const altro: ProductionRow[] = [];
    for (const p of dayProductions) {
      if (p.meal_service === 'colazione') colazione.push(p);
      else if (p.meal_service === 'pranzo') pranzo.push(p);
      else if (p.meal_service === 'cena') cena.push(p);
      else altro.push(p);
    }
    return [
      { key: 'colazione', label: '☕ Colazione', items: colazione },
      { key: 'pranzo', label: '🍽️ Pranzo', items: pranzo },
      { key: 'cena', label: '🌙 Cena', items: cena },
      { key: 'na', label: 'Non assegnato', items: altro },
    ];
  }, [dayProductions]);

  // ───────── workflow stati (T-229 punto 6) ─────────
  // APERTA (closed_at null) → CHIUSA (closed_at set, verified_at null) →
  // VERIFICATA (verified_at set).
  const localDayPatch =
    localDayRef.current?.date === selectedDate
      ? localDayRef.current.patches
      : {};
  const effectiveClosedAt = (
    'closed_at' in localDayPatch
      ? localDayPatch.closed_at
      : dayRow?.closed_at
  ) as string | null | undefined;
  const effectiveVerifiedAt = (
    'verified_at' in localDayPatch
      ? localDayPatch.verified_at
      : dayRow?.verified_at
  ) as string | null | undefined;
  const closed = !!effectiveClosedAt;
  const verified = !!effectiveVerifiedAt;
  const canSupervise =
    profile?.role === 'ristoratore' ||
    profile?.role === 'consulente' ||
    profile?.is_platform_owner === true;
  // Una firma congela lo snapshot per TUTTI. Il supervisore può modificare
  // soltanto dopo una transizione esplicita di reopen, che resta in audit.
  const editable = isProductionDayEditableV1({
    closedAt: effectiveClosedAt,
    verifiedAt: effectiveVerifiedAt,
  });

  // Stampanti fisiche (driver 'preview' escluso).
  const realPrinters = useMemo<PrinterRow[]>(
    () => dbPrinters.filter((p) => p.driver !== 'preview' && p.active !== false),
    [dbPrinters],
  );
  // T-424: la stampante ANTEPRIMA è sempre disponibile come opzione DEMO per il
  // prelievo campione (utile nelle dimostrazioni e su PWA senza hardware). NON
  // stampa un'etichetta reale e NON registra un prelievo: mostra solo l'anteprima.
  const PREVIEW_ROW: PrinterRow = useMemo(
    () => ({ id: PREVIEW_PRINTER_ID, name: `${PREVIEW_PRINTER_NAME} (demo)`, driver: 'preview', connection: 'usb', address: 'preview://canvas' }),
    [],
  );
  const samplePrinters = useMemo<PrinterRow[]>(() => [...realPrinters, PREVIEW_ROW], [realPrinters, PREVIEW_ROW]);
  const selectedPrinter = useMemo<PrinterRow | null>(() => {
    // Default: prima stampante reale; se nessuna, l'anteprima (demo).
    return samplePrinters.find((p) => p.id === samplePrinterId) ?? realPrinters[0] ?? PREVIEW_ROW;
  }, [samplePrinters, realPrinters, samplePrinterId, PREVIEW_ROW]);

  // ───────── checklist blocchi chiusura (T-229 punto 1) ─────────
  const checks = useMemo<Record<string, ProdCheck>>(() => {
    const m: Record<string, ProdCheck> = {};
    for (const p of dayProductions) {
      // T-422: per i piatti serviti freddi la temperatura al cuore è FACOLTATIVA
      // → il check 'temp' è considerato soddisfatto anche se assente.
      const cold = isColdProd(p);
      const temp = cold || p.ccp_core_temp_c != null;
      const portionsOk = p.portions != null && p.portions > 0;
      const service = p.meal_service != null && MEAL_SERVICES.includes(p.meal_service);
      const sample = (sampleLocal[p.id] ?? p.sample_taken_at) != null;
      // T-433: il prelievo campione NON blocca più la chiusura (non è
      // obbligatorio nel manuale dell'azienda). Resta come check informativo.
      m[p.id] = { temp, portions: portionsOk, service, sample, ok: temp && portionsOk && service };
    }
    return m;
  }, [dayProductions, sampleLocal, isColdProd]);

  const missingParts = useMemo<string[]>(() => {
    let temp = 0;
    let portionsN = 0;
    let service = 0;
    let sample = 0;
    for (const p of dayProductions) {
      const c = checks[p.id];
      if (!c) continue;
      if (!c.temp) temp += 1;
      if (!c.portions) portionsN += 1;
      if (!c.service) service += 1;
      if (!c.sample) sample += 1;
    }
    const fmt = (n: number, what: string): string =>
      `${n} ${n === 1 ? 'prodotto' : 'prodotti'} senza ${what}`;
    const parts: string[] = [];
    if (temp > 0) parts.push(fmt(temp, 'temperatura al cuore'));
    if (portionsN > 0) parts.push(fmt(portionsN, 'porzioni'));
    if (service > 0) parts.push(fmt(service, 'servizio (pranzo/cena)'));
    // T-433: il campione NON è più bloccante → non entra nei "parti mancanti"
    // che impediscono la chiusura. `sample` resta calcolato per il solo display.
    void sample;
    return parts;
  }, [dayProductions, checks]);

  const allOk = missingParts.length === 0;

  // Somma porzioni → pasti totali auto-calcolati (T-229 punto 5).
  const portionsSum = useMemo(
    () => dayProductions.reduce((acc, p) => acc + (p.portions ?? 0), 0),
    [dayProductions],
  );

  const sampledCount = useMemo(
    () => dayProductions.filter((p) => checks[p.id]?.sample).length,
    [dayProductions, checks],
  );
  const allSampled = dayProductions.length > 0 && sampledCount === dayProductions.length;
  const lastSampleAt = useMemo<string | null>(() => {
    let max: string | null = null;
    for (const p of dayProductions) {
      const s = sampleLocal[p.id] ?? p.sample_taken_at ?? null;
      if (s && (!max || s > max)) max = s;
    }
    return max;
  }, [dayProductions, sampleLocal]);

  // ───────── warning DDT (T-229 punto 7, non bloccante) ─────────
  const hasDdtForDay = useMemo(() => {
    return shipments.some((s) => {
      if (s.archived_at) return false;
      if ((s.kind ?? 'ddt') !== 'ddt') return false;
      if (s.status === 'void') return false;
      if (!stockEventMatchesBusiness(
        { business_id: s.business_id },
        businessId,
        allowLegacyUnscopedBusiness,
      )) return false;
      const issued = s.status === 'issued' || s.doc_number != null;
      if (!issued) return false;
      const ts = s.issued_at ?? s.created_at ?? null;
      return !!ts && localDateKeyFromIso(ts) === selectedDate;
    });
  }, [shipments, businessId, allowLegacyUnscopedBusiness, selectedDate]);

  // Re-inizializza i draft SOLO al cambio giornata. Se il dayRow appare a metà
  // editing (insert on-demand della riga), i draft restano: la riga è nata
  // proprio dal nostro patch e sovrascriverli butterebbe il testo in corso.
  const draftsKeyRef = useRef('');
  const mealsTotalManualRef = useRef(false);
  const autoMealsRef = useRef<string | null>(null);
  useEffect(() => {
    const key = `${selectedDate}|${dayRow?.id ?? ''}`;
    if (draftsKeyRef.current === key) return;
    const prevDate = draftsKeyRef.current.split('|')[0];
    draftsKeyRef.current = key;
    if (prevDate === selectedDate) return;
    setSampleNotesDraft(dayRow?.sample_meal_notes ?? '');
    setMealsTotalDraft(dayRow?.meals_total != null ? String(dayRow.meals_total) : '');
    setMealsNotesDraft(dayRow?.meals_notes ?? '');
    setNotesDraft(dayRow?.notes ?? '');
    mealsTotalManualRef.current = false;
    autoMealsRef.current = null;
  }, [selectedDate, dayRow]);

  // Auto-calcolo "Pasti totali" = somma porzioni. Aggiorna il draft finché
  // l'utente non lo modifica a mano (override manuale sempre possibile); il
  // valore (auto o manuale) viene firmato/persistito alla chiusura.
  useEffect(() => {
    if (!editable || mealsTotalManualRef.current) return;
    const auto = portionsSum > 0 ? String(portionsSum) : '';
    const cur = mealsTotalDraft.trim();
    const prevAuto = autoMealsRef.current;
    const curIsAuto = cur === '' || cur === auto || (prevAuto != null && cur === prevAuto);
    if (!curIsAuto) {
      // Valore preesistente diverso dal calcolato (es. override salvato in DB
      // in una sessione precedente) → non clobberare, segna come manuale.
      mealsTotalManualRef.current = true;
      return;
    }
    if (cur !== auto) setMealsTotalDraft(auto);
    autoMealsRef.current = auto;
  }, [portionsSum, editable, mealsTotalDraft]);

  function shiftDay(delta: number): void {
    const d = new Date(`${selectedDate}T12:00:00`);
    d.setDate(d.getDate() + delta);
    setSelectedDate(localDateKey(d));
  }

  function localDayPatches(): Record<string, unknown> {
    return localDayRef.current?.date === selectedDate ? localDayRef.current.patches : {};
  }

  /**
   * Crea on-demand la riga production_days alla prima modifica (op:'insert')
   * oppure applica il patch parziale alla riga esistente (op:'update' con
   * base_version corrente). Ritorna l'id della riga, o null su errore.
   */
  async function patchDay(patch: Record<string, unknown>): Promise<string | null> {
    if (!profile?.active_tenant_id || !businessId) {
      setToast('Nessuna sede attiva');
      return null;
    }
    const local = localDayRef.current?.date === selectedDate ? localDayRef.current : null;
    try {
      const existingId = dayRow?.id ?? local?.id ?? null;
      if (existingId) {
        // Base OCC: la version del mirror (che useEnqueueMaster auto-incrementa
        // a ogni enqueue: insert → 0, update → base+1) oppure quella tracciata
        // localmente se il mirror non è ancora stato riletto — la più alta
        // delle due. Nessun +1 manuale qui: il bump lo fa useEnqueueMaster.
        const baseVersion = Math.max(dayRow?.version ?? 0, local?.version ?? 0);
        await enqueueMaster.mutateAsync({
          table: 'production_days',
          id: existingId,
          op: 'update',
          base_version: baseVersion,
          patch: { id: existingId, tenant_id: profile.active_tenant_id, ...patch },
        });
        localDayRef.current = {
          date: selectedDate,
          id: existingId,
          version: baseVersion + 1,
          patches: { ...(local?.patches ?? {}), ...patch },
        };
        return existingId;
      }
      // Id DETERMINISTICO (stesso tenant+sede+data → stesso uuid): due device
      // che creano offline la stessa giornata generano lo STESSO id, quindi il
      // secondo insert è un replay idempotente lato server invece di una
      // violazione della UNIQUE (tenant, business, sheet_date) che perderebbe
      // in silenzio la scheda del secondo device.
      const id = await productionDayIdFor(profile.active_tenant_id, businessId, selectedDate);
      await enqueueMaster.mutateAsync({
        table: 'production_days',
        id,
        op: 'insert',
        base_version: 0,
        patch: {
          id,
          tenant_id: profile.active_tenant_id,
          business_id: businessId,
          sheet_date: selectedDate,
          sample_meal_taken: false,
          sample_meal_at: null,
          sample_meal_notes: null,
          meals_total: null,
          meals_notes: null,
          notes: null,
          closed_at: null,
          closed_by: null,
          verified_by: null,
          verified_at: null,
          reopened_at: null,
          reopen_count: 0,
          archived_at: null,
          ...patch,
        },
      });
      // Dopo l'insert il mirror ha row.version=0 → la prossima patch parte da
      // base 0 (convenzione sync-push), NON da 1.
      localDayRef.current = { date: selectedDate, id, version: 0, patches: { ...patch } };
      return id;
    } catch (err) {
      setToast(toUserMessage(err));
      return null;
    }
  }

  function commitTextField(
    field: 'sample_meal_notes' | 'meals_notes' | 'notes',
    draft: string,
  ): void {
    if (!editable) return;
    const value = draft.trim() || null;
    const lp = localDayPatches();
    const current = (field in lp ? lp[field] : dayRow?.[field] ?? null) as string | null;
    if (value === current) return;
    void patchDay({ [field]: value });
  }

  function commitMealsTotal(): void {
    if (!editable) return;
    const value = parseMealsTotal(mealsTotalDraft);
    if (value === 'invalid') {
      setToast('Numero pasti non valido');
      return;
    }
    const lp = localDayPatches();
    const current = ('meals_total' in lp ? lp.meals_total : dayRow?.meals_total ?? null) as
      | number
      | null;
    if (current === value) return;
    void patchDay({ meals_total: value });
  }

  // T-423: salvataggio ESPLICITO della scheda. I campi di giornata si scrivono
  // già on-blur, ma se l'utente esce/chiude senza togliere il focus dal campo il
  // draft resta solo in memoria → questo bottone forza la scrittura di TUTTI i
  // draft, così non si perde nulla.
  function saveDaySheet(): void {
    if (!editable) {
      setToast(verified ? 'Scheda verificata: dati non modificabili' : 'Scheda chiusa: dati non modificabili');
      return;
    }
    commitTextField('sample_meal_notes', sampleNotesDraft);
    commitMealsTotal();
    commitTextField('meals_notes', mealsNotesDraft);
    commitTextField('notes', notesDraft);
    setToast('Scheda salvata ✓');
  }

  // Base OCC per i patch alle productions (mirror vs versioni tracciate
  // localmente dopo enqueue ravvicinati — vedi localProdVerRef sopra).
  function prodBaseVersion(p: ProductionRow): number {
    return Math.max(p.version ?? 0, localProdVerRef.current[p.id] ?? 0);
  }

  function openIndustrial(p: ProductionRow): void {
    setProdForm({
      portions: p.portions != null ? String(p.portions) : '',
      mealService: p.meal_service ?? '',
      ccpTemp: p.ccp_core_temp_c != null ? String(p.ccp_core_temp_c) : '',
      ccpNotes: p.ccp_notes ?? '',
    });
    setEditingProd(p);
  }

  async function saveIndustrial(): Promise<void> {
    if (!editingProd) return;
    if (!editable) {
      setToast(verified ? 'Scheda verificata: dati non modificabili' : 'Scheda chiusa: dati non modificabili');
      return;
    }
    if (!profile?.active_tenant_id) {
      setToast('Nessuna sede attiva');
      return;
    }
    let portions: number | null = null;
    if (prodForm.portions.trim()) {
      const n = Number(prodForm.portions.replace(',', '.'));
      if (!Number.isFinite(n) || n < 0) {
        setToast('Porzioni non valide');
        return;
      }
      portions = Math.round(n);
    }
    let ccpTemp: number | null = null;
    if (prodForm.ccpTemp.trim()) {
      const n = Number(prodForm.ccpTemp.replace(',', '.'));
      if (!Number.isFinite(n)) {
        setToast('Temperatura non valida');
        return;
      }
      ccpTemp = n;
    }
    try {
      const base = prodBaseVersion(editingProd);
      await enqueueMaster.mutateAsync({
        table: 'productions',
        id: editingProd.id,
        op: 'update',
        base_version: base,
        patch: {
          id: editingProd.id,
          tenant_id: profile.active_tenant_id,
          portions,
          meal_service: prodForm.mealService || null,
          ccp_core_temp_c: ccpTemp,
          ccp_notes: prodForm.ccpNotes.trim() || null,
        },
      });
      localProdVerRef.current[editingProd.id] = base + 1;
      setToast('Dati industriali salvati');
      setEditingProd(null);
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  // ───────── prelievo campione + stampa etichetta (T-229 punto 4) ─────────
  async function printSample(p: ProductionRow): Promise<void> {
    if (!editable) {
      setToast('Scheda non modificabile');
      return;
    }
    if (!profile?.active_tenant_id || !businessId) {
      setToast('Nessuna sede attiva');
      return;
    }
    if (!selectedPrinter) {
      setToast('Configura una stampante nelle Impostazioni');
      return;
    }
    setBusySampleId(p.id);
    try {
      const nowIso = new Date().toISOString();
      const labelData = buildCampioneLabelData({
        product: p.output_name,
        lp_code: p.lp_code ?? null,
        prelievo_at: nowIso,
      });
      // 1) stampa fisica (UNA copia: un campione per produzione)
      const adapter = createPrinterAdapter(selectedPrinter.driver);
      const session = await adapter.connect({
        id: selectedPrinter.id,
        name: selectedPrinter.name,
        driver: selectedPrinter.driver,
        connection: selectedPrinter.connection,
        address: selectedPrinter.address,
      });
      await adapter.printLabel(
        session,
        {
          id: CAMPIONE_TEMPLATE.id,
          tenant_id: '',
          name: CAMPIONE_TEMPLATE.name,
          preset: CAMPIONE_TEMPLATE.preset,
          width_mm: CAMPIONE_TEMPLATE.width_mm,
          height_mm: CAMPIONE_TEMPLATE.height_mm,
          elements: CAMPIONE_TEMPLATE.elements as never,
          required_allergens: CAMPIONE_TEMPLATE.required_allergens,
        } as never,
        labelData,
        1,
      );
      // T-424: la stampante ANTEPRIMA è solo DEMO → mostra l'etichetta a schermo
      // ma NON registra un prelievo campione reale (sarebbe un record HACCP
      // falso). Ci si ferma qui: nessun sample_taken_at, nessun override locale.
      if (selectedPrinter.driver === 'preview') {
        setToast('Anteprima campione (demo) — nessuna etichetta reale, prelievo NON registrato');
        return;
      }
      // 2) marca il prelievo sul master productions (OCC)
      const base = prodBaseVersion(p);
      await enqueueMaster.mutateAsync({
        table: 'productions',
        id: p.id,
        op: 'update',
        base_version: base,
        patch: {
          id: p.id,
          tenant_id: profile.active_tenant_id,
          sample_taken_at: nowIso,
        },
      });
      localProdVerRef.current[p.id] = base + 1;
      setSampleLocal((prev) => ({ ...prev, [p.id]: nowIso }));
      // 3) T-279: NIENTE evento label_printed separato per il campione → non
      // intasa lo Storico etichette. Il prelievo è già registrato sul master
      // productions (sample_taken_at, sopra: è anche il gate di chiusura giornata)
      // e la stampa fisica è avvenuta. Il campione è "integrato" nel lotto di
      // base ed è ristampabile dalla sezione apposita del dialog di ristampa.
      setToast(`Campione di "${p.output_name}" stampato e registrato`);
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setBusySampleId(null);
    }
  }

  async function closeDay(): Promise<void> {
    const userId = profile?.id ?? null;
    const tenantId = profile?.active_tenant_id ?? null;
    if (!userId) {
      setToast('Utente non identificato');
      return;
    }
    if (!tenantId || !businessId) {
      setToast('Nessuna sede attiva');
      return;
    }
    if (dayConflict) {
      setToast('Risolvi prima il conflitto di sincronizzazione di questa giornata.');
      return;
    }
    if (closed) return;
    // Blocchi alla chiusura: ogni produzione deve avere temperatura al cuore
    // (salvo ricetta fredda), porzioni > 0 e servizio. Il campione resta
    // informativo secondo la policy T-433.
    if (!allOk) {
      setToast(`Non puoi chiudere: ${missingParts.join(', ')}`);
      return;
    }
    // Payload costruito dai valori CORRENTI della UI, NON da dayRow: dopo un
    // commit-on-blur appena fatto React Query può essere stantio e firmerebbe
    // nell'evento un totale pasti / stato campione vecchio.
    const parsed = parseMealsTotal(mealsTotalDraft);
    if (parsed === 'invalid') {
      setToast('Numero pasti non valido');
      return;
    }
    const mealsTotal = parsed ?? (portionsSum > 0 ? portionsSum : null);
    // sample_meal_taken di testata è DERIVATO (T-229 punto 5): true quando
    // tutte le produzioni hanno il campione.
    const sampleTaken = allSampled;
    setBusyClose(true);
    try {
      const dayId =
        dayRow?.id ?? (await productionDayIdFor(tenantId, businessId, selectedDate));
      const now = new Date().toISOString();
      const snapshot = normalizeProductionDaySignedSnapshotV1({
        sample_meal_taken: sampleTaken,
        sample_meal_at: sampleTaken ? lastSampleAt : null,
        sample_meal_notes: sampleNotesDraft,
        meals_total: mealsTotal,
        meals_notes: mealsNotesDraft,
        notes: notesDraft,
        reopen_count: ((
          'reopen_count' in localDayPatches()
            ? localDayPatches().reopen_count
            : dayRow?.reopen_count
        ) as number | undefined) ?? 0,
      });
      // Lo snapshot completo entra direttamente nell'atomic outbox. Eventuali
      // vecchi commit-on-blur della stessa testata vengono assorbiti nello
      // stesso enqueue SQLite: nessun master predecessor separato può
      // sopravvivere a un crash o correre davanti alla firma.
      const local = localDayRef.current?.date === selectedDate
        ? localDayRef.current
        : null;
      const expectedVersion = Math.max(
        dayRow?.version ?? 0,
        local?.version ?? 0,
      );
      await getSyncEngine().queueProductionDayTransition({
        transition: 'close',
        requestId: crypto.randomUUID(),
        tenantId,
        businessId,
        productionDayId: dayId,
        sheetDate: selectedDate,
        expectedVersion,
        deviceId: (await getMeta('device_id')) ?? 'unknown',
        requestedAt: now,
        snapshot,
        actorUserId: userId,
      });
      localDayRef.current = {
        date: selectedDate,
        id: dayId,
        version: expectedVersion + 1,
        patches: {
          ...(local?.patches ?? {}),
          closed_at: now,
          closed_by: userId,
        },
      };
      await queryClient.invalidateQueries({ queryKey: ['masters'] });
      localDayRef.current = null;
      setToast(
        typeof navigator !== 'undefined' && !navigator.onLine
          ? 'Scheda chiusa sul dispositivo: firma atomica accodata e protetta fino alla sincronizzazione.'
          : 'Scheda chiusa sul dispositivo e in sincronizzazione atomica — in attesa di verifica.',
      );
    } catch (err) {
      await refreshDayConflictNow().catch(() => undefined);
      setToast(toUserMessage(err));
    } finally {
      setBusyClose(false);
    }
  }

  function currentSignedSnapshot(): ProductionDaySignedSnapshotV1 | null {
    const lp = localDayPatches();
    if (!dayRow && !localDayRef.current) return null;
    return normalizeProductionDaySignedSnapshotV1({
      sample_meal_taken: ((
        'sample_meal_taken' in lp
          ? lp.sample_meal_taken
          : dayRow?.sample_meal_taken
      ) as boolean | undefined) ?? false,
      sample_meal_at: ((
        'sample_meal_at' in lp
          ? lp.sample_meal_at
          : dayRow?.sample_meal_at
      ) as string | null | undefined) ?? null,
      sample_meal_notes: ((
        'sample_meal_notes' in lp
          ? lp.sample_meal_notes
          : dayRow?.sample_meal_notes
      ) as string | null | undefined) ?? null,
      meals_total: ((
        'meals_total' in lp
          ? lp.meals_total
          : dayRow?.meals_total
      ) as number | null | undefined) ?? null,
      meals_notes: ((
        'meals_notes' in lp
          ? lp.meals_notes
          : dayRow?.meals_notes
      ) as string | null | undefined) ?? null,
      notes: ((
        'notes' in lp
          ? lp.notes
          : dayRow?.notes
      ) as string | null | undefined) ?? null,
      reopen_count: ((
        'reopen_count' in lp
          ? lp.reopen_count
          : dayRow?.reopen_count
      ) as number | undefined) ?? 0,
    });
  }

  async function queueExistingDayTransition(
    transition: 'verify' | 'reopen',
  ): Promise<boolean> {
    const tenantId = profile?.active_tenant_id ?? null;
    const actorUserId = profile?.id ?? null;
    const local = localDayRef.current?.date === selectedDate
      ? localDayRef.current
      : null;
    const dayId = dayRow?.id ?? local?.id ?? null;
    const snapshot = currentSignedSnapshot();
    if (!tenantId || !actorUserId || !businessId || !dayId || !snapshot) {
      setToast('Scheda locale non disponibile: aggiorna i dati e riprova');
      return false;
    }
    const expectedVersion = Math.max(
      dayRow?.version ?? 0,
      local?.version ?? 0,
    );
    const now = new Date().toISOString();
    await getSyncEngine().queueProductionDayTransition({
      transition,
      requestId: crypto.randomUUID(),
      tenantId,
      businessId,
      productionDayId: dayId,
      sheetDate: selectedDate,
      expectedVersion,
      deviceId: (await getMeta('device_id')) ?? 'unknown',
      requestedAt: now,
      snapshot,
      actorUserId,
    });
    localDayRef.current = {
      date: selectedDate,
      id: dayId,
      version: expectedVersion + 1,
      patches: {
        ...(local?.patches ?? {}),
        ...(transition === 'verify'
          ? {
              verified_at: now,
              verified_by: actorUserId,
            }
          : {
              closed_at: null,
              closed_by: null,
              reopened_at: now,
              reopen_count: snapshot.reopen_count + 1,
            }),
      },
    };
    await queryClient.invalidateQueries({ queryKey: ['masters'] });
    localDayRef.current = null;
    return true;
  }

  async function resolveDayConflict(): Promise<void> {
    const tenantId = profile?.active_tenant_id ?? null;
    if (!tenantId || !businessId) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      setToast('Serve connessione per rileggere e adottare la scheda autorevole dal server.');
      return;
    }
    setBusyConflictResolution(true);
    try {
      await getSyncEngine().resolveProductionDayConflict({
        tenantId,
        businessId,
        sheetDate: selectedDate,
      });
      localDayRef.current = null;
      await queryClient.invalidateQueries({ queryKey: ['masters'] });
      setDayConflict(null);
      setToast('Conflitto risolto: caricata la scheda autorevole dal server. Ora puoi ripetere l’operazione.');
    } catch (err) {
      setToast(`Conflitto non risolto: ${toUserMessage(err)}`);
    } finally {
      setBusyConflictResolution(false);
    }
  }

  async function refreshDayConflictNow(): Promise<void> {
    const tenantId = profile?.active_tenant_id ?? null;
    if (!tenantId || !businessId) return;
    const conflict = await getSyncEngine().latestProductionDayConflict({
      tenantId,
      businessId,
      sheetDate: selectedDate,
    });
    setDayConflict(
      conflict
        ? { reason: conflict.conflict?.reason ?? 'conflitto server' }
        : null,
    );
    if (conflict) {
      localDayRef.current = null;
      await queryClient.invalidateQueries({ queryKey: ['masters'] });
    }
  }

  // ───────── riapertura (T-229 punto 6b, solo supervisore) ─────────
  async function reopenDay(): Promise<void> {
    if (dayConflict) {
      setToast('Risolvi prima il conflitto di sincronizzazione di questa giornata.');
      return;
    }
    if (!canSupervise || !closed || verified) return;
    try {
      if (await queueExistingDayTransition('reopen')) {
        setToast(
          typeof navigator !== 'undefined' && !navigator.onLine
            ? 'Riapertura accodata sul dispositivo; dopo la sincronizzazione potrai correggere e richiudere.'
            : 'Riapertura in sincronizzazione — la modifica resta tracciata e richiederà una nuova firma.',
        );
      }
    } catch (err) {
      await refreshDayConflictNow().catch(() => undefined);
      setToast(toUserMessage(err));
    }
  }

  // ───────── verifica 2° livello (T-229 punto 6c) ─────────
  function closedByCurrent(): string | null {
    const lp = localDayPatches();
    return ('closed_by' in lp ? lp.closed_by : dayRow?.closed_by ?? null) as string | null;
  }

  function onVerifyClick(): void {
    if (profile?.id && closedByCurrent() === profile.id) {
      setToast('La verifica di 2° livello va fatta da una persona DIVERSA da chi ha chiuso la scheda');
      return;
    }
    setVerifyConfirmOpen(true);
  }

  async function verifyDay(): Promise<void> {
    if (dayConflict) {
      setToast('Risolvi prima il conflitto di sincronizzazione di questa giornata.');
      return;
    }
    if (!profile?.id || !canSupervise || !closed || verified) return;
    if (closedByCurrent() === profile.id) {
      setToast('La verifica di 2° livello va fatta da una persona DIVERSA da chi ha chiuso la scheda');
      return;
    }
    try {
      if (await queueExistingDayTransition('verify')) {
        setToast(
          typeof navigator !== 'undefined' && !navigator.onLine
            ? 'Verifica firmata sul dispositivo e accodata in modo atomico.'
            : 'Verifica firmata sul dispositivo e in sincronizzazione atomica.',
        );
      }
    } catch (err) {
      await refreshDayConflictNow().catch(() => undefined);
      setToast(toUserMessage(err));
    }
  }

  async function exportDay(emailTo?: string): Promise<void> {
    if (!businessId) {
      setToast('Nessuna sede attiva');
      return;
    }
    if (!navigator.onLine) {
      setToast('Serve connessione per esportare la scheda. Riprova quando sei online: i dati restano salvati.');
      return;
    }
    setBusyExport(true);
    try {
      const { data } = await getSupabase().auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error('Non autenticato');
      const res = await fetch(
        `${SUPABASE_URL.replace(/\/$/, '')}/functions/v1/export-produzione-giorno`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            business_id: businessId,
            date: selectedDate,
            ...(emailTo ? { email_to: emailTo } : {}),
          }),
        },
      );
      if (!res.ok) {
        let detail = '';
        try {
          const j = (await res.clone().json()) as { detail?: { message?: string }; error?: string };
          detail = j?.detail?.message ?? j?.error ?? '';
        } catch {
          detail = await res.text().catch(() => '');
        }
        throw new Error(`HTTP ${res.status}${detail ? ` — ${detail}` : ''}`);
      }
      if (emailTo) {
        // L'edge function risponde JSON con `email_sent`: true solo se l'invio
        // è stato accettato dal provider. HTTP 200 NON basta: il PDF può
        // essere stato generato con email fallita → niente toast di successo
        // incondizionato.
        let emailSent = false;
        try {
          const j = (await res.json()) as { email_sent?: boolean };
          emailSent = j.email_sent === true;
        } catch {
          // Risposta senza JSON → invio non confermato.
        }
        setToast(
          emailSent
            ? `Scheda inviata a ${emailTo}`
            : 'PDF generato, ma invio email NON riuscito (scaricalo con "Esporta PDF" e invialo manualmente)',
        );
        return;
      }
      // L'edge function può rispondere col PDF inline oppure con JSON
      // contenente la signed URL del file caricato su Storage.
      const contentType = res.headers.get('content-type') ?? '';
      let blob: Blob;
      if (contentType.includes('application/json')) {
        const j = (await res.json()) as { url?: string; signed_url?: string; pdf_url?: string };
        const url = j.url ?? j.signed_url ?? j.pdf_url;
        if (!url) throw new Error('Risposta del server senza PDF');
        const pdfRes = await fetch(url);
        if (!pdfRes.ok) throw new Error(`Download PDF fallito (HTTP ${pdfRes.status})`);
        blob = await pdfRes.blob();
      } else {
        blob = await res.blob();
      }
      const fileName = `trackeat-produzione-${selectedDate}.pdf`;
      if (Capacitor.isNativePlatform()) {
        // Su Android/iOS <a download> non funziona nella WebView: scriviamo in
        // Cache e apriamo lo Share sheet nativo (pattern ReportsPage).
        const base64 = await blobToBase64(blob);
        const written = await Filesystem.writeFile({
          path: fileName,
          data: base64,
          directory: Directory.Cache,
          recursive: true,
        });
        await Share.share({
          title: 'Scheda di produzione',
          url: written.uri,
          dialogTitle: 'Salva o condividi la scheda',
        });
        setToast('Scheda pronta — scegli dove salvarla');
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
        setToast('Scheda scaricata');
      }
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setBusyExport(false);
    }
  }

  if (!collettivaEnabled) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start"><IonBackButton defaultHref="/today" /></IonButtons>
            <IonTitle>Scheda di produzione</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <p style={{ color: '#64748b' }}>
            Modulo collettiva non attivo per questa azienda. Contatta l&apos;assistenza TrackEAT per attivarlo.
          </p>
        </IonContent>
      </IonPage>
    );
  }

  const mealsParsed = parseMealsTotal(mealsTotalDraft);
  const mealsOverride =
    mealsParsed !== 'invalid' && mealsParsed != null && portionsSum > 0 && mealsParsed !== portionsSum;
  const mealsOk = mealsParsed !== 'invalid' && mealsParsed != null && mealsParsed > 0;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref="/today" /></IonButtons>
          <IonTitle>Scheda di produzione</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton aria-label="Giorno precedente" onClick={() => shiftDay(-1)}>
              <IonIcon icon={chevronBackOutline} />
            </IonButton>
          </IonButtons>
          <IonButton
            expand="full"
            fill="clear"
            onClick={() => setDateOpen(true)}
            style={{ textTransform: 'none', fontWeight: 600 }}
          >
            <IonIcon icon={calendarOutline} slot="start" />
            {formatDateLabel(selectedDate)}
          </IonButton>
          <IonButtons slot="end">
            <IonButton aria-label="Giorno successivo" onClick={() => shiftDay(1)}>
              <IonIcon icon={chevronForwardOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent style={{ '--background': '#eef2f7' } as React.CSSProperties}>
        <div style={{ padding: '4px 16px 48px' }}>
          {dayConflict && (
            <div style={warnBannerStyle} role="alert">
              <div style={{ fontWeight: 800 }}>
                Operazione sulla scheda non applicata dal server
              </div>
              <div style={{ fontSize: 12, marginTop: 4 }}>
                La copia ottimistica è stata ritirata. Motivo: {dayConflict.reason}.
                Rileggi la verità server prima di ripetere chiusura, verifica o riapertura.
              </div>
              <IonButton
                size="small"
                fill="outline"
                color="warning"
                disabled={
                  busyConflictResolution
                  || (typeof navigator !== 'undefined' && !navigator.onLine)
                }
                onClick={() => void resolveDayConflict()}
                style={{ marginTop: 8 }}
              >
                {busyConflictResolution
                  ? <IonSpinner name="dots" />
                  : 'Rileggi e risolvi conflitto'}
              </IonButton>
              {typeof navigator !== 'undefined' && !navigator.onLine && (
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  Serve connessione: il conflitto resta salvato e non verrà perso al riavvio.
                </div>
              )}
            </div>
          )}
          {/* ── Stato scheda ── */}
          {verified && dayRow && (
            <div style={successBannerStyle}>
              <div style={{ fontWeight: 800, color: '#065f46', display: 'flex', alignItems: 'center', gap: 6, fontSize: 15 }}>
                <IonIcon icon={shieldCheckmarkOutline} />
                VERIFICATA (2° livello)
              </div>
              <div style={{ fontSize: 12, color: '#047857', marginTop: 4 }}>
                Chiusa il {dayRow.closed_at ? formatDateTime(dayRow.closed_at) : '—'} · Verificata il{' '}
                {dayRow.verified_at ? formatDateTime(dayRow.verified_at) : '—'}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                Scheda in sola lettura per tutti.
              </div>
            </div>
          )}
          {closed && !verified && dayRow && (
            <div style={infoBannerStyle}>
              <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, color: '#1e40af' }}>
                <IonIcon icon={lockClosedOutline} />
                CHIUSA il {dayRow.closed_at ? formatDateTime(dayRow.closed_at) : '—'}
              </div>
              <div style={{ fontSize: 12, marginTop: 4 }}>
                In attesa di verifica di 2° livello da parte di una persona diversa da chi ha chiuso.
              </div>
              {canSupervise && (
                <div style={{ fontSize: 12, marginTop: 4, fontWeight: 600 }}>
                  Per correggere i dati devi prima riaprire esplicitamente la scheda (sezione 4).
                </div>
              )}
              {(dayRow.reopen_count ?? 0) > 0 && (
                <div style={{ fontSize: 12, marginTop: 4, color: '#64748b' }}>
                  Riaperture precedenti: {dayRow.reopen_count}
                </div>
              )}
            </div>
          )}
          {!closed && dayRow?.reopened_at && (
            <div style={warnBannerStyle}>
              Scheda riaperta il {formatDateTime(dayRow.reopened_at)}
              {(dayRow.reopen_count ?? 0) > 0 ? ` (${dayRow.reopen_count}ª riapertura)` : ''} — completa le
              correzioni e richiudila per firmarla di nuovo.
            </div>
          )}

          {/* ── 1 · PRODUZIONI DEL GIORNO ── */}
          <div style={sectionTitleStyle}>1 · Produzioni del giorno ({dayProductions.length})</div>
          {dayProductions.length === 0 ? (
            <div style={{ ...cardStyle, color: '#64748b', fontStyle: 'italic', fontSize: 13 }}>
              {collettivaEnabled && selectedDate >= COLLETTIVA_PLAN_ONLY_FROM
                ? 'Nessuna produzione pianificata per questo giorno. La scheda si popola da "Pianifica produzione" (ordini accettati → salva il piano del giorno).'
                : 'Nessuna lavorazione registrata in questo giorno. I pasti creati in "Lavorazioni" compaiono qui.'}
            </div>
          ) : (
            groups.map((g) =>
              g.items.length === 0 ? null : (
                <div key={g.key}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '12px 2px 2px' }}>
                    {g.label} ({g.items.length})
                  </div>
                  {g.items.map((p) => {
                    const check = checks[p.id] ?? { temp: false, portions: false, service: false, sample: false, ok: false };
                    // T-422: piatto servito freddo → nessun riferimento 74 °C, nessun allarme.
                    const cold = isColdProd(p);
                    const ccpOk = cold || (p.ccp_core_temp_c != null && p.ccp_core_temp_c >= 74);
                    const inputs = flattenInputLots(p.inputs ?? []);
                    const sampleIso = sampleLocal[p.id] ?? p.sample_taken_at ?? null;
                    return (
                      <div
                        key={p.id}
                        style={{
                          ...cardStyle,
                          padding: 0,
                          overflow: 'hidden',
                          borderLeft: `4px solid ${check.ok ? '#10b981' : '#f59e0b'}`,
                        }}
                      >
                        <div
                          role="button"
                          onClick={() => openIndustrial(p)}
                          style={{ padding: '12px 14px 8px', cursor: 'pointer' }}
                        >
                          <div style={{ fontWeight: 600, fontSize: 15, color: '#0f172a' }}>
                            {p.output_name}
                            {p.menu_label && <span style={menuBadgeStyle}>{p.menu_label}</span>}
                            {p.source_production_plan_item_id && <span style={menuBadgeStyle}>📋 dal piano</span>}
                            {p.prestampa && !p.source_production_plan_item_id && <span style={menuBadgeStyle}>🌙 pre-stampata</span>}
                          </div>
                          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                            {/* T-406: chiarito — previsti (dal piano) vs pasti PRODOTTI (reali). */}
                            {p.output_qty != null ? `previsti ${p.output_qty} · ` : ''}
                            {p.portions != null ? `prodotti ${p.portions}` : 'prodotti: da indicare'}
                            {p.meal_service ? ` · ${MEAL_LABELS[p.meal_service]}` : ''}
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              marginTop: 2,
                              color: cold ? '#047857' : p.ccp_core_temp_c == null ? '#94a3b8' : ccpOk ? '#047857' : '#b45309',
                            }}
                          >
                            {cold
                              ? (p.ccp_core_temp_c == null
                                  ? 'CCP freddo: servito refrigerato — temperatura al cuore non richiesta'
                                  : `CCP freddo: ${p.ccp_core_temp_c} °C al cuore (servito freddo)`)
                              : (p.ccp_core_temp_c == null
                                  ? 'CCP cottura: temperatura al cuore da registrare'
                                  : `CCP cottura: ${p.ccp_core_temp_c} °C al cuore${ccpOk ? ' ✓' : ' — sotto il riferimento 74 °C'}`)}
                          </div>
                        </div>
                        {/* Checklist requisiti chiusura (✓ verde / ! ambra) */}
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '4px 8px',
                            padding: '0 14px 8px',
                          }}
                        >
                          <CheckChip ok={check.temp} label="Temp. al cuore" />
                          <CheckChip ok={check.portions} label="Pasti prodotti" />
                          <CheckChip ok={check.service} label="Servizio" />
                          <CheckChip ok={check.sample} label="Campione" />
                        </div>
                        {/* Pasto campione per prodotto */}
                        <div style={{ padding: '8px 14px 10px', borderTop: '1px solid #f1f5f9' }}>
                          {sampleIso ? (
                            <div style={{ fontSize: 13, color: '#047857', fontWeight: 600 }}>
                              ✓ Campione prelevato · {formatTime(sampleIso)}
                            </div>
                          ) : editable ? (
                            <IonButton
                              size="small"
                              fill="outline"
                              disabled={busySampleId != null}
                              onClick={() => void printSample(p)}
                            >
                              {busySampleId === p.id ? (
                                <IonSpinner name="dots" />
                              ) : (
                                <>
                                  <IonIcon icon={printOutline} slot="start" />
                                  Preleva campione + stampa etichetta
                                </>
                              )}
                            </IonButton>
                          ) : (
                            <div style={{ fontSize: 13, color: '#b45309', fontWeight: 600 }}>
                              ! Campione non prelevato
                            </div>
                          )}
                        </div>
                        {inputs.length > 0 && (
                          <IonAccordionGroup>
                            <IonAccordion value="ingredienti">
                              <IonItem slot="header" lines="none" style={{ '--min-height': '36px' } as React.CSSProperties}>
                                <IonLabel style={{ fontSize: 13, color: '#475569' }}>
                                  Ingredienti e lotti ({inputs.length})
                                </IonLabel>
                              </IonItem>
                              <div slot="content" style={{ padding: '0 14px 12px', fontSize: 13, color: '#475569' }}>
                                {inputs.map((i, idx) => (
                                  <div
                                    key={idx}
                                    style={{
                                      padding: '5px 0',
                                      borderBottom: idx < inputs.length - 1 ? '1px solid #f1f5f9' : 'none',
                                    }}
                                  >
                                    <span style={{ fontWeight: 600 }}>{i.food_name ?? '—'}</span>
                                    {' — lotto '}
                                    {i.batch_lot ?? '—'}
                                    {i.qty_used != null ? ` — ${i.qty_used} ${i.uom ?? ''}` : ''}
                                    {i.supplier_name ? ` — ${i.supplier_name}` : ''}
                                  </div>
                                ))}
                              </div>
                            </IonAccordion>
                          </IonAccordionGroup>
                        )}
                      </div>
                    );
                  })}
                </div>
              ),
            )
          )}

          {/* ── 2 · PASTO CAMPIONE ── */}
          <div style={sectionTitleStyle}>2 · Pasto campione</div>
          <div style={infoBannerStyle}>
            Per ogni preparazione va conservato un campione ({SAMPLE_NOTES_PLACEHOLDER}). Usa il bottone
            &quot;Preleva campione&quot; su ciascun prodotto qui sopra: stampa l&apos;etichetta e registra l&apos;orario.
          </div>
          <div
            style={{
              ...cardStyle,
              borderLeft: `4px solid ${
                dayProductions.length === 0 ? '#cbd5e1' : allSampled ? '#10b981' : '#f59e0b'
              }`,
            }}
          >
            {dayProductions.length === 0 ? (
              <div style={{ fontSize: 13, color: '#64748b', fontStyle: 'italic' }}>
                Nessuna produzione registrata: il campione si preleva prodotto per prodotto.
              </div>
            ) : allSampled ? (
              <div style={{ fontSize: 14, color: '#047857', fontWeight: 700 }}>
                ✓ Campione prelevato per tutte le produzioni ({sampledCount}/{dayProductions.length})
                {lastSampleAt ? ` · ultimo alle ${formatTime(lastSampleAt)}` : ''}
              </div>
            ) : (
              <div style={{ fontSize: 14, color: '#b45309', fontWeight: 700 }}>
                ! Campioni prelevati: {sampledCount}/{dayProductions.length} — mancano{' '}
                {dayProductions.length - sampledCount}
              </div>
            )}
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
              Lo stato &quot;campione prelevato&quot; della giornata è calcolato automaticamente e viene
              registrato alla chiusura.
            </div>
            {/* Stampante per l'etichetta campione (T-424: l'anteprima demo è
                sempre disponibile come opzione, utile in dimostrazione/PWA). */}
            {realPrinters.length === 0 && (
              <div style={{ fontSize: 12, color: '#b45309', marginTop: 10 }}>
                Nessuna stampante reale configurata — puoi usare l&apos;<strong>Anteprima (demo)</strong> qui
                sotto per le dimostrazioni; per stampare etichette campione reali configura una stampante nelle
                Impostazioni.
              </div>
            )}
            <IonItem lines="none" style={{ '--padding-start': '0' } as React.CSSProperties}>
              <IonSelect
                label="Stampante etichetta campione"
                labelPlacement="stacked"
                value={selectedPrinter?.id ?? null}
                onIonChange={(ev) => setSamplePrinterId(String(ev.detail.value ?? ''))}
              >
                {samplePrinters.map((pr) => (
                  <IonSelectOption key={pr.id} value={pr.id}>{pr.name}</IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>
            {selectedPrinter?.driver === 'preview' && (
              <div style={{ fontSize: 12, color: '#6b3fa0', marginTop: 4 }}>
                🖥️ Anteprima demo: l&apos;etichetta campione viene mostrata a schermo, <strong>non</strong> stampata,
                e <strong>non</strong> viene registrata come prelievo reale.
              </div>
            )}
            <IonItem lines="none" style={{ '--padding-start': '0' } as React.CSSProperties}>
              <IonLabel position="stacked">Note campione</IonLabel>
              <IonTextarea
                rows={2}
                value={sampleNotesDraft}
                placeholder={SAMPLE_NOTES_PLACEHOLDER}
                disabled={!editable}
                onIonInput={(ev) => setSampleNotesDraft(String(ev.detail.value ?? ''))}
                onIonBlur={() => commitTextField('sample_meal_notes', sampleNotesDraft)}
              />
            </IonItem>
          </div>

          {/* ── 3 · RIEPILOGO E NOTE ── */}
          <div style={sectionTitleStyle}>3 · Riepilogo e note</div>
          <div style={{ ...cardStyle, borderLeft: `4px solid ${mealsOk ? '#10b981' : '#f59e0b'}` }}>
            <IonItem lines="none" style={{ '--padding-start': '0' } as React.CSSProperties}>
              <IonLabel position="stacked">Pasti totali prodotti</IonLabel>
              <IonInput
                type="number"
                inputmode="numeric"
                value={mealsTotalDraft}
                placeholder={portionsSum > 0 ? String(portionsSum) : 'es. 320'}
                disabled={!editable}
                onIonInput={(ev) => {
                  mealsTotalManualRef.current = true;
                  setMealsTotalDraft(String(ev.detail.value ?? ''));
                }}
                onIonBlur={() => commitMealsTotal()}
              />
            </IonItem>
            <div style={{ fontSize: 12, color: '#64748b', margin: '2px 2px 8px' }}>
              Calcolato dalle porzioni: {portionsSum} {portionsSum === 1 ? 'pasto' : 'pasti'}
              {mealsOverride ? ' · override manuale' : ''}
            </div>
            <IonItem lines="none" style={{ '--padding-start': '0' } as React.CSSProperties}>
              <IonLabel position="stacked">Note sui pasti</IonLabel>
              <IonTextarea
                rows={2}
                value={mealsNotesDraft}
                disabled={!editable}
                onIonInput={(ev) => setMealsNotesDraft(String(ev.detail.value ?? ''))}
                onIonBlur={() => commitTextField('meals_notes', mealsNotesDraft)}
              />
            </IonItem>
            <IonItem lines="none" style={{ '--padding-start': '0' } as React.CSSProperties}>
              <IonLabel position="stacked">Note di giornata</IonLabel>
              <IonTextarea
                rows={2}
                value={notesDraft}
                disabled={!editable}
                onIonInput={(ev) => setNotesDraft(String(ev.detail.value ?? ''))}
                onIonBlur={() => commitTextField('notes', notesDraft)}
              />
            </IonItem>
            {/* T-423: salvataggio esplicito — forza la scrittura di tutti i campi
                anche se l'utente non ha tolto il focus. */}
            {editable && (
              <IonButton expand="block" fill="outline" style={{ marginTop: 10 }} onClick={() => saveDaySheet()}>
                <IonIcon icon={saveOutline} slot="start" /> Salva scheda
              </IonButton>
            )}
          </div>

          {/* ── 4 · CHIUSURA E VERIFICA ── */}
          <div style={sectionTitleStyle}>4 · Chiusura e verifica</div>

          {dayProductions.length > 0 && !hasDdtForDay && (
            <div style={warnBannerStyle}>
              ⚠ Nessun DDT emesso per questa giornata. Se i pasti vengono trasportati, emetti il DDT da
              &quot;Spedizioni&quot; (non blocca la chiusura).
            </div>
          )}

          {/* Azioni secondarie */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '10px 0 0' }}>
            <IonButton size="small" fill="outline" disabled={busyExport} onClick={() => void exportDay()}>
              {busyExport ? <IonSpinner name="dots" /> : (
                <>
                  <IonIcon icon={documentTextOutline} slot="start" /> Esporta PDF
                </>
              )}
            </IonButton>
            <IonButton size="small" fill="outline" disabled={busyExport} onClick={() => setEmailAlertOpen(true)}>
              <IonIcon icon={mailOutline} slot="start" /> Invia email
            </IonButton>
            <IonButton size="small" fill="outline" routerLink="/warehouse/shipments">
              <IonIcon icon={arrowForwardOutline} slot="start" /> Spedizioni
            </IonButton>
            {closed && !verified && canSupervise && (
              <IonButton
                size="small"
                fill="outline"
                color="warning"
                disabled={!!dayConflict || busyConflictResolution}
                onClick={() => setReopenConfirmOpen(true)}
              >
                <IonIcon icon={lockOpenOutline} slot="start" /> Riapri scheda
              </IonButton>
            )}
          </div>

          {/* CTA primaria contestuale allo stato */}
          {!closed && (
            <>
              <IonButton
                expand="block"
                color="danger"
                style={primaryCtaStyle}
                disabled={!allOk || busyClose || !!dayConflict}
                onClick={() => setCloseConfirmOpen(true)}
              >
                {busyClose ? <IonSpinner name="dots" /> : (
                  <>
                    <IonIcon icon={lockClosedOutline} slot="start" /> CHIUDI SCHEDA
                  </>
                )}
              </IonButton>
              {!allOk && (
                <div style={{ ...warnBannerStyle, marginTop: 8 }}>
                  <div style={{ fontWeight: 700 }}>Non puoi chiudere:</div>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {missingParts.map((m) => (
                      <li key={m} style={{ marginBottom: 2 }}>{m}</li>
                    ))}
                  </ul>
                  <div style={{ fontSize: 12, marginTop: 6 }}>
                    Tocca un prodotto nella sezione 1 per completare i dati mancanti.
                  </div>
                </div>
              )}
            </>
          )}
          {closed && !verified && canSupervise && (
            <IonButton
              expand="block"
              color="success"
              style={primaryCtaStyle}
              disabled={!!dayConflict || busyConflictResolution}
              onClick={() => onVerifyClick()}
            >
              <IonIcon icon={shieldCheckmarkOutline} slot="start" /> CONFERMA VERIFICA (2° LIVELLO)
            </IonButton>
          )}
          {closed && !verified && !canSupervise && (
            <div style={{ fontSize: 12, color: '#64748b', margin: '12px 2px 0' }}>
              In attesa di verifica di 2° livello da parte di un responsabile (titolare o consulente).
            </div>
          )}
        </div>

        {/* Selettore data */}
        <IonModal isOpen={dateOpen} onDidDismiss={() => setDateOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Scegli il giorno</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setDateOpen(false)}><IonIcon icon={closeOutline} /></IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <IonDatetime
              presentation="date"
              locale="it-IT"
              firstDayOfWeek={1}
              value={selectedDate}
              onIonChange={(ev) => {
                const v = ev.detail.value;
                if (typeof v === 'string' && v.length >= 10) {
                  setSelectedDate(v.slice(0, 10));
                  setDateOpen(false);
                }
              }}
            />
            <IonButton
              expand="block"
              fill="outline"
              onClick={() => {
                setSelectedDate(localDateKey(new Date()));
                setDateOpen(false);
              }}
            >
              Oggi
            </IonButton>
          </IonContent>
        </IonModal>

        {/* Modale dati industriali del pasto */}
        <IonModal isOpen={!!editingProd} onDidDismiss={() => setEditingProd(null)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Dati industriali</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setEditingProd(null)}><IonIcon icon={closeOutline} /></IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {editingProd && (() => {
              const portionsFieldOk = (() => {
                const n = Number(prodForm.portions.replace(',', '.'));
                return prodForm.portions.trim() !== '' && Number.isFinite(n) && n > 0;
              })();
              const serviceFieldOk = (MEAL_SERVICES as readonly string[]).includes(prodForm.mealService);
              // T-422: se la ricetta è "servita fredda" la temperatura è facoltativa
              // (il campo risulta ok anche vuoto; se compilato deve essere un numero).
              const editingCold = isColdProd(editingProd);
              const tempFieldOk = (() => {
                const n = Number(prodForm.ccpTemp.replace(',', '.'));
                if (prodForm.ccpTemp.trim() === '') return editingCold;
                return Number.isFinite(n);
              })();
              const reqWrap = (ok: boolean): React.CSSProperties => ({
                borderLeft: `3px solid ${ok ? '#10b981' : '#f59e0b'}`,
                borderRadius: 6,
                paddingLeft: 8,
                marginBottom: 8,
              });
              return (
                <>
                  <h2 style={{ fontSize: 17, marginTop: 0 }}>
                    {editingProd.output_name}
                    {editingProd.menu_label && <span style={menuBadgeStyle}>{editingProd.menu_label}</span>}
                  </h2>
                  <p style={{ fontSize: 13, color: '#64748b', marginTop: 0 }}>
                    {editingProd.output_qty != null
                      ? `${editingProd.output_qty} ${editingProd.output_uom ?? ''} · `
                      : ''}
                    prodotto il {formatDateTime(editingProd.produced_at)}
                  </p>
                  {editingProd.menu_label && (
                    <p style={{ fontSize: 12, color: '#64748b', marginTop: 0 }}>
                      Il menù si imposta sulla ricetta, non qui.
                    </p>
                  )}
                  {verified && (
                    <p style={{ fontSize: 13, color: '#b45309' }}>
                      Scheda verificata: dati in sola lettura.
                    </p>
                  )}
                  {closed && !verified && (
                    <p style={{ fontSize: 13, color: '#92400e' }}>
                      Scheda chiusa: dati in sola lettura. Un responsabile deve riaprirla prima di qualsiasi correzione.
                    </p>
                  )}
                  <div style={reqWrap(portionsFieldOk)}>
                    <IonItem lines="none">
                      <IonLabel position="stacked">Pasti prodotti (reali) *</IonLabel>
                      <IonInput
                        type="number"
                        inputmode="numeric"
                        value={prodForm.portions}
                        disabled={!editable}
                        onIonInput={(ev) => setProdForm((f) => ({ ...f, portions: String(ev.detail.value ?? '') }))}
                      />
                    </IonItem>
                  </div>
                  <div style={reqWrap(serviceFieldOk)}>
                    <IonItem lines="none">
                      <IonSelect
                        label="Servizio *"
                        labelPlacement="stacked"
                        value={prodForm.mealService}
                        disabled={!editable}
                        onIonChange={(ev) =>
                          setProdForm((f) => ({ ...f, mealService: (ev.detail.value ?? '') as MealService | '' }))
                        }
                      >
                        <IonSelectOption value="">— non assegnato</IonSelectOption>
                        {MEAL_SERVICES.map((s) => (
                          <IonSelectOption key={s} value={s}>{MEAL_LABELS[s]}</IonSelectOption>
                        ))}
                      </IonSelect>
                    </IonItem>
                  </div>
                  <div style={reqWrap(tempFieldOk)}>
                    <IonItem lines="none">
                      <IonLabel position="stacked">CCP — temperatura al cuore (°C){editingCold ? '' : ' *'}</IonLabel>
                      <IonInput
                        type="number"
                        inputmode="decimal"
                        value={prodForm.ccpTemp}
                        disabled={!editable}
                        placeholder={editingCold ? 'facoltativa (servito freddo)' : ''}
                        onIonInput={(ev) => setProdForm((f) => ({ ...f, ccpTemp: String(ev.detail.value ?? '') }))}
                      />
                    </IonItem>
                  </div>
                  <IonNote style={{ display: 'block', fontSize: 12, margin: '0 2px 8px' }}>
                    {editingCold
                      ? 'Piatto servito freddo: la temperatura al cuore è facoltativa e non blocca la chiusura.'
                      : 'Riferimento: ≥ 74 °C al cuore per 15 secondi (CCP cottura). I campi con * sono obbligatori per chiudere la scheda.'}
                  </IonNote>
                  <IonItem>
                    <IonLabel position="stacked">Note aggiuntive (facoltative)</IonLabel>
                    <IonTextarea
                      rows={2}
                      value={prodForm.ccpNotes}
                      disabled={!editable}
                      onIonInput={(ev) => setProdForm((f) => ({ ...f, ccpNotes: String(ev.detail.value ?? '') }))}
                    />
                  </IonItem>
                  <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
                    <IonButton
                      expand="block"
                      style={{ flex: 1 }}
                      disabled={!editable}
                      onClick={() => void saveIndustrial()}
                    >
                      Salva
                    </IonButton>
                    <IonButton expand="block" fill="outline" style={{ flex: 1 }} onClick={() => setEditingProd(null)}>
                      Annulla
                    </IonButton>
                  </div>
                </>
              );
            })()}
          </IonContent>
        </IonModal>

        {/* T-424 — Anteprima campione (demo) in-app */}
        <IonModal isOpen={inAppPreview !== null} onDidDismiss={() => setInAppPreview(null)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Anteprima etichetta campione (demo)</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setInAppPreview(null)}>Chiudi</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {inAppPreview && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 13, color: '#6b3fa0', textAlign: 'center', background: '#f3eefb', border: '1px solid #ddd0f0', borderRadius: 10, padding: '8px 12px' }}>
                  🖥️ Anteprima dimostrativa · {inAppPreview.widthMm}×{inAppPreview.heightMm} mm. Non è un&apos;etichetta
                  stampata e il prelievo <strong>non</strong> è stato registrato.
                </div>
                <img
                  src={inAppPreview.png}
                  alt="Anteprima etichetta campione"
                  style={{ maxWidth: '100%', border: '1px solid #cbd5e1', borderRadius: 6, boxShadow: '0 2px 10px rgba(0,0,0,.12)' }}
                />
              </div>
            )}
          </IonContent>
        </IonModal>

        {/* Conferma chiusura scheda */}
        <IonAlert
          isOpen={closeConfirmOpen}
          header="Chiudere la scheda?"
          message={`La scheda del ${formatDateLabel(selectedDate)} verrà chiusa e firmata da te (${dayProductions.length} lavorazioni, ${parseMealsTotal(mealsTotalDraft) !== 'invalid' && parseMealsTotal(mealsTotalDraft) != null ? parseMealsTotal(mealsTotalDraft) : portionsSum} pasti). Dopo la chiusura sarà in sola lettura; per correggerla un responsabile dovrà prima riaprirla esplicitamente.`}
          buttons={[
            { text: 'Annulla', role: 'cancel' },
            {
              text: 'Chiudi scheda',
              role: 'destructive',
              handler: () => {
                void closeDay();
              },
            },
          ]}
          onDidDismiss={() => setCloseConfirmOpen(false)}
        />

        {/* Conferma verifica 2° livello */}
        <IonAlert
          isOpen={verifyConfirmOpen}
          header="Verifica di 2° livello"
          message="Confermi di aver riesaminato la scheda di produzione (lotti, CCP, campioni, totali)? La verifica verrà firmata con il tuo utente e la scheda diventerà definitiva (sola lettura)."
          buttons={[
            { text: 'Annulla', role: 'cancel' },
            {
              text: 'Conferma verifica',
              handler: () => {
                void verifyDay();
              },
            },
          ]}
          onDidDismiss={() => setVerifyConfirmOpen(false)}
        />

        {/* Conferma riapertura */}
        <IonAlert
          isOpen={reopenConfirmOpen}
          header="Riaprire la scheda?"
          message="La chiusura attuale verrà annullata: la scheda torna modificabile e andrà richiusa (nuova firma). La riapertura resta tracciata."
          buttons={[
            { text: 'Annulla', role: 'cancel' },
            {
              text: 'Riapri scheda',
              role: 'destructive',
              handler: () => {
                void reopenDay();
              },
            },
          ]}
          onDidDismiss={() => setReopenConfirmOpen(false)}
        />

        {/* Invio email */}
        <IonAlert
          isOpen={emailAlertOpen}
          header="Invia scheda via email"
          message="Inserisci l'indirizzo del destinatario."
          inputs={[
            {
              name: 'email',
              type: 'email',
              placeholder: 'destinatario@esempio.it',
            },
          ]}
          buttons={[
            { text: 'Annulla', role: 'cancel' },
            {
              text: 'Invia',
              handler: (data: { email?: string }) => {
                const email = (data.email ?? '').trim();
                if (!email || !email.includes('@')) {
                  setToast('Email non valida');
                  return;
                }
                void exportDay(email);
              },
            },
          ]}
          onDidDismiss={() => setEmailAlertOpen(false)}
        />

        <IonToast isOpen={!!toast} message={toast ?? ''} duration={2600} onDidDismiss={() => setToast(null)} />
      </IonContent>
    </IonPage>
  );
}
